import { Navigate } from "react-router-dom";
import { useAuth, AppRole } from "@/shared/hooks/use-auth";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: AppRole[];
  redirectTo?: string;
  /** If true, also enforces that the user's staff role is is_active=true (server-validated) */
  requireActive?: boolean;
}

const ProtectedRoute = ({
  children,
  allowedRoles,
  redirectTo = "/login",
  requireActive = true,
}: ProtectedRouteProps) => {
  const { user, roles, isActive, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) return <Navigate to={redirectTo} replace />;

  if (allowedRoles && allowedRoles.length > 0 && roles.length === 0) {
    return <Navigate to="/onboard" replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const hasAccess = roles.some((r) => allowedRoles.includes(r.role));
    if (!hasAccess) return <Navigate to="/" replace />;
  }

  // Block inactive staff at route level — server-validated via is_staff_active() RPC.
  // Super admins are never blocked by this check (they don't rely on is_active).
  // Guest and non-staff users are also unaffected.
  const isStaffRole = roles.some((r) =>
    r.role === "hotel_admin" || r.role === "hotel_staff"
  );
  const isSuperAdmin = roles.some((r) => r.role === "super_admin");

  if (requireActive && isStaffRole && !isSuperAdmin && !isActive) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ reason: "inactive" }}
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
